import { useState, useEffect, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Text, Button, Badge, Spinner, Field, Input, Dropdown, Option, Switch } from '@fluentui/react-components';
import { ArrowLeftRegular, ClipboardTaskListLtrRegular, ErrorCircleRegular, WarningRegular } from '@fluentui/react-icons';
import {
  getQuestionnaire, submitAnswer, completeQuestionnaire,
  type QuestionnaireView, type QuestionView,
} from '../services/questionnaire-service';
import { useApiToken } from '../hooks/useApiToken';
import { getBadgeProps, QUESTIONNAIRE_STATUS_BADGE } from '../utils/badge-config';
import type { Project } from '../services/project-service';

/** The single intake definition shipped today (config/questionnaires/migrate-intake.json). */
const QUESTIONNAIRE_ID = 'migrate-intake';

interface QuestionnairePageProps {
  project: Project;
  onBack: () => void;
}

/**
 * Section-stepper wizard over the questionnaire engine (orchestrator RFC §4.4).
 * Every answer commits individually through the server's validate→store→write-back
 * pipeline (the ApplianceChecklistCard idiom — no client-side progression state):
 * choice/boolean controls commit on change, text/number on blur or Enter. The
 * server response drives errors, write-back warnings, and a full view refresh so
 * dependsOn visibility never drifts from the stored answers.
 */
export function QuestionnairePage({ project, onBack }: QuestionnairePageProps) {
  const intl = useIntl();
  const getToken = useApiToken();
  const [view, setView] = useState<QuestionnaireView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingQuestionId, setSavingQuestionId] = useState<string | null>(null);
  const [questionErrors, setQuestionErrors] = useState<Record<string, string[]>>({});
  const [warnings, setWarnings] = useState<string[]>([]);
  const [completing, setCompleting] = useState(false);
  const [completeBlockers, setCompleteBlockers] = useState<string[]>([]);
  // Monotonic load sequence — a stale response (project switched mid-flight) must not land.
  const seqRef = useRef(0);

  const load = useCallback(async (showSpinner: boolean) => {
    if (!project.id) return;
    const seq = ++seqRef.current;
    if (showSpinner) setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const fresh = await getQuestionnaire(project.id, QUESTIONNAIRE_ID, project.customerId, token);
      if (seq !== seqRef.current) return;
      setView(fresh);
    } catch (err) {
      if (seq !== seqRef.current) return;
      // Only the initial/explicit load (showSpinner) replaces the wizard with the error panel.
      // A failed post-commit refresh keeps the wizard standing — the answer is already saved
      // server-side and the next interaction re-syncs (the sibling background-load idiom).
      if (showSpinner) setError(err instanceof Error ? err.message : 'Failed to load questionnaire.');
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  }, [project.id, project.customerId, getToken]);

  useEffect(() => {
    setView(null);
    setActiveSectionId(null);
    setDrafts({});
    setQuestionErrors({});
    setWarnings([]);
    setCompleteBlockers([]);
    load(true);
  }, [load]);

  const sections = view?.sections ?? [];
  // Resume on the server-recorded section; user navigation (activeSectionId) wins once set.
  const resumeSectionId = view?.currentSectionId && sections.some(s => s.id === view.currentSectionId)
    ? view.currentSectionId
    : sections[0]?.id ?? null;
  const currentSectionId = activeSectionId && sections.some(s => s.id === activeSectionId)
    ? activeSectionId
    : resumeSectionId;
  const sectionIndex = sections.findIndex(s => s.id === currentSectionId);
  const section = sectionIndex >= 0 ? sections[sectionIndex] : null;

  const commit = useCallback(async (questionId: string, value: string) => {
    if (!project.id) return;
    setSavingQuestionId(questionId);
    setCompleteBlockers([]);
    try {
      const token = await getToken();
      const result = await submitAnswer(project.id, QUESTIONNAIRE_ID, project.customerId, questionId, value, token);
      if (result.saved) {
        setQuestionErrors(prev => { const next = { ...prev }; delete next[questionId]; return next; });
        setDrafts(prev => { const next = { ...prev }; delete next[questionId]; return next; });
        setWarnings(result.warnings);
        await load(false); // refresh visibility, counts, prefills, status
      } else {
        setWarnings([]);
        setQuestionErrors(prev => ({ ...prev, [questionId]: result.errors }));
      }
    } catch (err) {
      setQuestionErrors(prev => ({
        ...prev,
        [questionId]: [err instanceof Error ? err.message : 'Failed to save answer.'],
      }));
    } finally {
      setSavingQuestionId(null);
    }
  }, [project.id, project.customerId, getToken, load]);

  /** Commit a text/number draft on blur/Enter — skipped when nothing changed. */
  const commitDraft = useCallback((qv: QuestionView) => {
    const q = qv.question;
    const draft = drafts[q.id];
    if (draft === undefined) return;
    const stored = qv.answerValue ?? '';
    if (draft.trim() === stored) {
      setDrafts(prev => { const next = { ...prev }; delete next[q.id]; return next; });
      return;
    }
    commit(q.id, draft);
  }, [drafts, commit]);

  const handleComplete = useCallback(async () => {
    if (!project.id || completing) return;
    setCompleting(true);
    setCompleteBlockers([]);
    try {
      const token = await getToken();
      const result = await completeQuestionnaire(project.id, QUESTIONNAIRE_ID, project.customerId, token);
      if (result.saved) {
        setWarnings([]);
        await load(false);
      } else {
        setCompleteBlockers(result.errors);
      }
    } catch (err) {
      setCompleteBlockers([err instanceof Error ? err.message : 'Failed to complete questionnaire.']);
    } finally {
      setCompleting(false);
    }
  }, [project.id, project.customerId, completing, getToken, load]);

  const setDraft = (id: string, value: string) =>
    setDrafts(prev => ({ ...prev, [id]: value }));

  const renderControl = (qv: QuestionView) => {
    const q = qv.question;
    const saving = savingQuestionId === q.id;

    switch (q.type) {
      case 'choice': {
        const selected = (q.options ?? []).find(o => o.value === qv.answerValue);
        return (
          <Dropdown
            value={selected?.label ?? ''}
            selectedOptions={qv.answerValue != null ? [qv.answerValue] : []}
            disabled={saving}
            placeholder={q.required ? undefined : intl.formatMessage({ id: 'questionnaire.placeholder.optional' })}
            onOptionSelect={(_e, d) => {
              if (d.optionValue && d.optionValue !== qv.answerValue) commit(q.id, d.optionValue);
            }}
          >
            {(q.options ?? []).map(o => (
              <Option key={o.value} value={o.value}>{o.label}</Option>
            ))}
          </Dropdown>
        );
      }

      case 'boolean':
        return (
          <Switch
            checked={qv.answerValue === 'true'}
            disabled={saving}
            label={qv.answerValue == null
              ? intl.formatMessage({ id: 'questionnaire.boolean.unanswered' })
              : qv.answerValue === 'true'
                ? intl.formatMessage({ id: 'questionnaire.boolean.yes' })
                : intl.formatMessage({ id: 'questionnaire.boolean.no' })}
            onChange={(_e, d) => commit(q.id, d.checked ? 'true' : 'false')}
          />
        );

      default: // text | number
        return (
          <Input
            type={q.type === 'number' ? 'number' : 'text'}
            value={drafts[q.id] ?? qv.answerValue ?? ''}
            disabled={saving}
            placeholder={q.required ? undefined : intl.formatMessage({ id: 'questionnaire.placeholder.optional' })}
            onChange={(_e, d) => setDraft(q.id, d.value)}
            onBlur={() => commitDraft(qv)}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLElement).blur(); }}
          />
        );
    }
  };

  return (
    <div className="page-root page-root--scrollable">
      <div className="page-title-bar">
        <Button appearance="subtle" icon={<ArrowLeftRegular />} onClick={onBack} title={intl.formatMessage({ id: 'questionnaire.backButton.title' })} />
        <ClipboardTaskListLtrRegular className="icon-title text-brand" />
        <Text size={500} weight="semibold">{intl.formatMessage({ id: 'questionnaire.title' }, { name: project.name })}</Text>
        <div className="title-bar-actions">
          {view && (
            <>
              <Badge size="small" appearance="outline">
                {intl.formatMessage({ id: 'questionnaire.progress' }, { answered: view.answered, total: view.visibleTotal })}
              </Badge>
              <Badge size="small" {...getBadgeProps(QUESTIONNAIRE_STATUS_BADGE, view.status)}>{view.status}</Badge>
              {view.status !== 'complete' && (
                <Button appearance="primary" size="small" disabled={completing} onClick={handleComplete}>
                  {completing
                    ? intl.formatMessage({ id: 'questionnaire.btn.completing' })
                    : intl.formatMessage({ id: 'questionnaire.btn.complete' })}
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {loading && (
        <div className="page-loading">
          <Spinner size="large" label={intl.formatMessage({ id: 'questionnaire.loading' })} />
        </div>
      )}

      {!loading && error && (
        <div className="page-error">
          <ErrorCircleRegular />
          <Text className="text-danger">{error}</Text>
          <Button appearance="primary" onClick={() => load(true)}>{intl.formatMessage({ id: 'questionnaire.retryButton' })}</Button>
        </div>
      )}

      {!loading && !error && view && section && (
        <>
          {view.description && <Text className="text-muted">{view.description}</Text>}

          {/* Section stepper — ApplianceChecklistCard idiom: done/current/upcoming by position. */}
          <div className="flex-row flex-wrap mt-sm">
            {sections.map((s, i) => (
              <Badge
                key={s.id}
                size="small"
                appearance={i === sectionIndex ? 'filled' : 'tint'}
                color={i < sectionIndex ? 'success' : i === sectionIndex ? 'informative' : 'subtle'}
              >
                {s.title}
              </Badge>
            ))}
          </div>

          {completeBlockers.length > 0 && (
            <div className="flex-col mt-sm">
              <Text weight="semibold" className="text-danger">{intl.formatMessage({ id: 'questionnaire.blockers.heading' })}</Text>
              {completeBlockers.map(b => <Text key={b} className="text-danger">{b}</Text>)}
            </div>
          )}

          {warnings.length > 0 && (
            <div className="flex-col mt-sm">
              {warnings.map(w => (
                <div key={w} className="flex-row">
                  <WarningRegular className="text-warning" />
                  <Text className="text-warning">{w}</Text>
                </div>
              ))}
            </div>
          )}

          <div className="section-header mt-md">
            <Text size={400} weight="semibold">{section.title}</Text>
            <Badge size="small" appearance="outline">
              {intl.formatMessage(
                { id: 'questionnaire.progress' },
                {
                  answered: section.questions.filter(qv => qv.visible && qv.answerValue != null).length,
                  total: section.questions.filter(qv => qv.visible).length,
                },
              )}
            </Badge>
          </div>

          <div className="flex-col mt-sm">
            {section.questions.filter(qv => qv.visible).map(qv => {
              const q = qv.question;
              const errs = questionErrors[q.id];
              return (
                <div key={q.id} className="flex-col">
                  <Field
                    label={q.prompt}
                    required={q.required}
                    hint={q.hint}
                    validationState={errs ? 'error' : 'none'}
                    validationMessage={errs ? errs.join(' ') : undefined}
                  >
                    {renderControl(qv)}
                  </Field>
                  {qv.prefill != null && qv.answerValue == null && (
                    <div className="flex-row">
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={savingQuestionId === q.id}
                        onClick={() => commit(q.id, qv.prefill!)}
                      >
                        {intl.formatMessage({ id: 'questionnaire.prefill.apply' }, { value: qv.prefill })}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex-row mt-md">
            <Button
              size="small"
              disabled={sectionIndex <= 0}
              onClick={() => setActiveSectionId(sections[sectionIndex - 1].id)}
            >
              {intl.formatMessage({ id: 'questionnaire.btn.previous' })}
            </Button>
            <Button
              size="small"
              disabled={sectionIndex >= sections.length - 1}
              onClick={() => setActiveSectionId(sections[sectionIndex + 1].id)}
            >
              {intl.formatMessage({ id: 'questionnaire.btn.next' })}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
